import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, useBlogApi } from '../hooks/useBlog';
import { Button } from '../components/ui/button';
import { 
  Plus, 
  Edit, 
  Trash2, 
  LogOut, 
  FileText, 
  Eye,
  EyeOff,
  Calendar,
  LayoutDashboard
} from 'lucide-react';

const AdminDashboard = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const { isAuthenticated, loading: authLoading, logout, checkAuth } = useAuth();
  const { adminGetPosts, deletePost } = useBlogApi();
  const navigate = useNavigate();

  useEffect(() => {
    const init = async () => {
      const isAuth = await checkAuth();
      if (!isAuth) {
        navigate('/admin/login');
        return;
      }
      loadPosts();
    };
    init();
  }, [checkAuth, navigate]);

  const loadPosts = async () => {
    try {
      setLoading(true);
      const status = filter === 'all' ? null : filter;
      const data = await adminGetPosts(1, 100, status);
      setPosts(data.posts);
    } catch (error) {
      console.error('Error loading posts:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      loadPosts();
    }
  }, [filter, isAuthenticated]);

  const handleDelete = async (postId) => {
    try {
      await deletePost(postId);
      setPosts(posts.filter(p => p.id !== postId));
      setDeleteConfirm(null);
    } catch (error) {
      console.error('Error deleting post:', error);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#F9F6F0] flex items-center justify-center">
        <p className="font-body text-[#6B6860]">Loading...</p>
      </div>
    );
  }

  const publishedCount = posts.filter(p => p.status === 'published').length;
  const draftCount = posts.filter(p => p.status === 'draft').length;

  return (
    <div className="min-h-screen bg-[#F9F6F0]">
      {/* Header */}
      <header className="bg-white border-b border-[#D4D1C9]">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="font-heading text-xl font-semibold text-[#2C4A3E] tracking-tight">
              Fairway<span className="text-[#B89A5A]"> Partners</span>
            </Link>
            <span className="text-[#D4D1C9]">|</span>
            <span className="font-body text-sm text-[#6B6860]">Blog Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/blog" className="font-body text-sm text-[#6B6860] hover:text-[#2C4A3E]">
              View Blog
            </Link>
            <button
              onClick={logout}
              data-testid="admin-logout-btn"
              className="flex items-center gap-2 font-body text-sm text-[#6B6860] hover:text-[#2C4A3E]"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white border border-[#D4D1C9] p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[#2C4A3E]/10 flex items-center justify-center">
                <FileText className="w-6 h-6 text-[#2C4A3E]" />
              </div>
              <div>
                <p className="font-body text-2xl font-semibold text-[#1A1A18]">{posts.length}</p>
                <p className="font-body text-sm text-[#6B6860]">Total Posts</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-[#D4D1C9] p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 flex items-center justify-center">
                <Eye className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="font-body text-2xl font-semibold text-[#1A1A18]">{publishedCount}</p>
                <p className="font-body text-sm text-[#6B6860]">Published</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-[#D4D1C9] p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-yellow-100 flex items-center justify-center">
                <EyeOff className="w-6 h-6 text-yellow-600" />
              </div>
              <div>
                <p className="font-body text-2xl font-semibold text-[#1A1A18]">{draftCount}</p>
                <p className="font-body text-sm text-[#6B6860]">Drafts</p>
              </div>
            </div>
          </div>
        </div>

        {/* Actions Bar */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 font-body text-sm ${filter === 'all' ? 'bg-[#2C4A3E] text-white' : 'bg-white border border-[#D4D1C9] text-[#6B6860]'}`}
            >
              All
            </button>
            <button
              onClick={() => setFilter('published')}
              className={`px-4 py-2 font-body text-sm ${filter === 'published' ? 'bg-[#2C4A3E] text-white' : 'bg-white border border-[#D4D1C9] text-[#6B6860]'}`}
            >
              Published
            </button>
            <button
              onClick={() => setFilter('draft')}
              className={`px-4 py-2 font-body text-sm ${filter === 'draft' ? 'bg-[#2C4A3E] text-white' : 'bg-white border border-[#D4D1C9] text-[#6B6860]'}`}
            >
              Drafts
            </button>
          </div>
          <Link to="/admin/posts/new">
            <Button 
              data-testid="new-post-btn"
              className="bg-[#2C4A3E] hover:bg-[#2C4A3E]/90 text-white font-body rounded-none"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Post
            </Button>
          </Link>
        </div>

        {/* Posts List */}
        <div className="bg-white border border-[#D4D1C9]">
          {loading ? (
            <div className="p-8 text-center">
              <p className="font-body text-[#6B6860]">Loading posts...</p>
            </div>
          ) : posts.length === 0 ? (
            <div className="p-12 text-center">
              <LayoutDashboard className="w-12 h-12 text-[#D4D1C9] mx-auto mb-4" />
              <p className="font-body text-[#6B6860] mb-4">No posts yet</p>
              <Link to="/admin/posts/new">
                <Button className="bg-[#2C4A3E] hover:bg-[#2C4A3E]/90 text-white font-body rounded-none">
                  Create your first post
                </Button>
              </Link>
            </div>
          ) : (
            <table className="w-full">
              <thead className="border-b border-[#D4D1C9] bg-[#F9F6F0]">
                <tr>
                  <th className="text-left px-6 py-4 font-body text-sm font-medium text-[#6B6860]">Title</th>
                  <th className="text-left px-6 py-4 font-body text-sm font-medium text-[#6B6860] hidden md:table-cell">Category</th>
                  <th className="text-left px-6 py-4 font-body text-sm font-medium text-[#6B6860]">Status</th>
                  <th className="text-left px-6 py-4 font-body text-sm font-medium text-[#6B6860] hidden md:table-cell">Date</th>
                  <th className="text-right px-6 py-4 font-body text-sm font-medium text-[#6B6860]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {posts.map((post) => (
                  <tr key={post.id} className="border-b border-[#D4D1C9] last:border-b-0 hover:bg-[#F9F6F0]/50">
                    <td className="px-6 py-4">
                      <p className="font-body text-sm font-medium text-[#1A1A18]">{post.title}</p>
                      <p className="font-body text-xs text-[#6B6860] mt-1">/{post.slug}</p>
                    </td>
                    <td className="px-6 py-4 hidden md:table-cell">
                      <span className="font-body text-sm text-[#6B6860]">{post.category}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-3 py-1 font-body text-xs font-medium ${
                        post.status === 'published' 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {post.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 hidden md:table-cell">
                      <div className="flex items-center gap-2 font-body text-sm text-[#6B6860]">
                        <Calendar className="w-4 h-4" />
                        {new Date(post.created_at).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <Link to={`/admin/posts/${post.id}/edit`}>
                          <button className="p-2 hover:bg-[#E8E4D9] rounded" title="Edit">
                            <Edit className="w-4 h-4 text-[#6B6860]" />
                          </button>
                        </Link>
                        {post.status === 'published' && (
                          <Link to={`/blog/${post.slug}`} target="_blank">
                            <button className="p-2 hover:bg-[#E8E4D9] rounded" title="View">
                              <Eye className="w-4 h-4 text-[#6B6860]" />
                            </button>
                          </Link>
                        )}
                        <button 
                          onClick={() => setDeleteConfirm(post.id)}
                          className="p-2 hover:bg-red-100 rounded" 
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-6 max-w-md w-full">
            <h3 className="font-heading text-xl font-medium text-[#1A1A18] mb-4">Delete Post?</h3>
            <p className="font-body text-[#6B6860] mb-6">
              This action cannot be undone. Are you sure you want to delete this post?
            </p>
            <div className="flex gap-4">
              <Button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 bg-white border border-[#D4D1C9] text-[#1A1A18] hover:bg-[#F9F6F0] font-body rounded-none"
              >
                Cancel
              </Button>
              <Button
                onClick={() => handleDelete(deleteConfirm)}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white font-body rounded-none"
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
